import { useState } from 'react'
import { useT } from '../i18n'
import { type HistoryPoint, fmtPrice } from '../lib/rates'

const W = 640
const H = 190
const PAD_X = 14
const PAD_TOP = 26
const PAD_BOTTOM = 26

const fmtDay = (s: string) =>
  new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

/** Premium 40ft-HC rate-trend chart — animated gradient area, live crosshair, Now/High/Low/Change. */
export function RateTrend({
  history,
  current,
}: { history: HistoryPoint[]; current?: number | null }) {
  const t = useT()
  const [hover, setHover] = useState<number | null>(null)
  const pts = history.filter((p) => p.median_40hc != null) as (HistoryPoint & {
    median_40hc: number
  })[]

  if (pts.length < 2) {
    return (
      <div className="card p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">{t('trend.title')}</h2>
          <span className="mono text-[11px] uppercase tracking-wide text-faint">40ft HC</span>
        </div>
        <p className="mt-3 text-sm text-muted">
          {t('trend.building')}
          {current != null && ` ${t('trend.today')}: ${fmtPrice(current)}.`}
        </p>
      </div>
    )
  }

  const values = pts.map((p) => p.median_40hc)
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const first = values[0] as number
  const last = values[values.length - 1] as number
  const delta = last - first
  const pct = first ? (delta / first) * 100 : 0
  // small headroom so a near-flat series still reads as a line
  const minV = lo - (hi - lo || lo * 0.04) * 0.35
  const maxV = hi + (hi - lo || lo * 0.04) * 0.35

  const x = (i: number) =>
    pts.length === 1 ? W / 2 : PAD_X + (i / (pts.length - 1)) * (W - 2 * PAD_X)
  const y = (v: number) =>
    H - PAD_BOTTOM - ((v - minV) / (maxV - minV || 1)) * (H - PAD_TOP - PAD_BOTTOM)

  const line = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.median_40hc).toFixed(1)}`)
    .join(' ')
  const area = `${line} L${x(pts.length - 1).toFixed(1)},${H - PAD_BOTTOM} L${x(0).toFixed(1)},${H - PAD_BOTTOM} Z`

  const up = delta > 0 // rate up = worse for shipper
  const deltaCls = delta === 0 ? 'text-muted' : up ? 'text-up' : 'text-down'
  const h = hover != null ? pts[hover] : null

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    let best = 0
    let bd = Number.POSITIVE_INFINITY
    pts.forEach((_, i) => {
      const d = Math.abs(x(i) - px)
      if (d < bd) {
        bd = d
        best = i
      }
    })
    setHover(best)
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">{t('trend.title')}</h2>
          <div className="mono text-[11px] uppercase tracking-wide text-faint">
            {t('trend.medianDays', { n: pts.length })}
          </div>
        </div>
        <div className="flex items-end gap-5 text-right">
          <Stat label="Now" value={fmtPrice(current ?? last)} accent />
          <Stat label="High" value={fmtPrice(hi)} />
          <Stat label="Low" value={fmtPrice(lo)} />
          <div>
            <div className="mono text-[10px] uppercase tracking-wide text-faint">Change</div>
            <div className={`mono tnum text-lg font-semibold ${deltaCls}`}>
              {delta === 0 ? '—' : `${up ? '▲' : '▼'} ${Math.abs(pct).toFixed(1)}%`}
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-4">
        {/* tooltip */}
        {h && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-border-strong bg-surface-0 px-2.5 py-1.5 text-xs card-shadow"
            style={{ left: `${(x(hover as number) / W) * 100}%`, top: 0 }}
          >
            <div className="tnum font-semibold text-signal">{fmtPrice(h.median_40hc)}</div>
            <div className="text-[10px] text-muted">{fmtDay(h.day)}</div>
          </div>
        )}
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          preserveAspectRatio="none"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label="40ft HC rate trend"
        >
          <title>40ft HC rate trend</title>
          <defs>
            <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--signal-rgb))" stopOpacity="0.32" />
              <stop offset="100%" stopColor="rgb(var(--signal-rgb))" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="trendLine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgb(var(--signal-rgb))" />
              <stop offset="100%" stopColor="rgb(var(--signal-2-rgb))" />
            </linearGradient>
          </defs>

          {/* faint baseline gridlines */}
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1="0"
              x2={W}
              y1={PAD_TOP + f * (H - PAD_TOP - PAD_BOTTOM)}
              y2={PAD_TOP + f * (H - PAD_TOP - PAD_BOTTOM)}
              stroke="rgb(var(--muted-rgb))"
              strokeOpacity="0.08"
            />
          ))}

          <path
            d={area}
            fill="url(#trendArea)"
            style={{ animation: 'fadeInArea 1.1s ease both' }}
          />
          <path
            d={line}
            fill="none"
            stroke="url(#trendLine)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 2000,
              animation: 'drawLine 1.1s cubic-bezier(0.2,0.7,0.2,1) forwards',
            }}
          />

          {/* points */}
          {pts.map((p, i) => (
            <circle
              key={p.day}
              cx={x(i)}
              cy={y(p.median_40hc)}
              r={i === pts.length - 1 ? 4.5 : 2.5}
              fill={i === pts.length - 1 ? 'rgb(var(--signal-rgb))' : 'rgb(var(--surface-0-rgb))'}
              stroke="rgb(var(--signal-rgb))"
              strokeWidth="1.5"
            />
          ))}

          {/* hover crosshair */}
          {h && (
            <g>
              <line
                x1={x(hover as number)}
                x2={x(hover as number)}
                y1={PAD_TOP - 8}
                y2={H - PAD_BOTTOM}
                stroke="rgb(var(--signal-rgb))"
                strokeOpacity="0.4"
                strokeDasharray="3 3"
              />
              <circle
                cx={x(hover as number)}
                cy={y(h.median_40hc)}
                r="5"
                fill="rgb(var(--signal-rgb))"
                stroke="rgb(var(--void-rgb))"
                strokeWidth="2"
              />
            </g>
          )}
        </svg>
        {/* x-axis endpoints */}
        <div className="mt-1 flex justify-between text-[10px] text-faint">
          <span>{fmtDay(pts[0]?.day ?? '')}</span>
          <span>{fmtDay(pts[pts.length - 1]?.day ?? '')}</span>
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-wide text-faint">{label}</div>
      <div className={`mono tnum text-lg font-semibold ${accent ? 'text-signal' : 'text-ink'}`}>
        {value}
      </div>
    </div>
  )
}
